import React from "react";
import { Pressable, ScrollView, StyleSheet, View, type PressableStateCallbackType } from "react-native";
import {
  CaretDownIcon,
  CaretRightIcon,
  CheckCircleIcon,
  DotsThreeIcon,
  StackSimpleIcon,
  XCircleIcon,
} from "@phosphor-icons/react";

import { PopoverMenu } from "../../../../../components/common/general/PopoverMenu/PopoverMenu";
import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import FilterPill from "./FilterPill";
import type {
  CourseEvaluatorReviewDecision,
  CourseEvaluatorReviewEffectiveness,
  CourseEvaluatorReviewItemType,
  CourseEvaluatorReviewData,
  CourseEvaluatorReviewSidebarMeta,
  CourseEvaluatorReviewSidebarTag,
} from "../types/reviewTypes";

interface ReviewSidebarProps {
  review: CourseEvaluatorReviewData;
  selectedItemId: string | null;
  filterMode: "all" | "undecided";
  collapsedSectionIds: Set<string>;
  getDecision: (id: string) => CourseEvaluatorReviewDecision;
  onSelectItem: (itemId: string | null) => void;
  onToggleSection: (sectionId: string) => void;
  onFilterModeChange: (mode: "all" | "undecided") => void;
}

function getTagPresentation(tag: CourseEvaluatorReviewSidebarTag) {
  switch (tag) {
    case "instructional":
      return {
        label: "Instructional",
        backgroundColor: "#E6F3FF",
        textColor: "#0069CC",
      };
    case "lecture":
      return {
        label: "Lecture",
        backgroundColor: "#F5F0FE",
        textColor: "#7244BB",
      };
    case "supplementary":
      return {
        label: "Supplementary",
        backgroundColor: "#F2F4F7",
        textColor: "#344054",
      };
    case "new":
    default:
      return {
        label: "New",
        backgroundColor: "#E7F7F8",
        textColor: "#15727D",
      };
  }
}

function getItemTypePresentation(itemType: CourseEvaluatorReviewItemType) {
  switch (itemType) {
    case "moduleDescription":
      return {
        label: "Module Description",
        color: "#7C3AED",
        icon: "moduleDescription" as const,
      };
    case "externalLink":
      return {
        label: "External Link",
        color: "#667085",
      };
    case "discussion":
      return {
        label: "Discussion",
        color: "#667085",
      };
    case "quiz":
      return {
        label: "Quiz",
        color: "#667085",
      };
    case "file":
      return {
        label: "File Topic",
        color: "#667085",
      };
    case "link":
      return {
        label: "External Link",
        color: "#667085",
      };
    case "htmlContent":
      return {
        label: "HTML Content",
        color: "#667085",
      };
    case "ile":
      return {
        label: "Axio ILE",
        color: "#667085",
      };
    case "checklist":
    default:
      return {
        label: "Checklist",
        color: "#667085",
      };
  }
}

function getEffectivenessColor(effectiveness: CourseEvaluatorReviewEffectiveness) {
  switch (effectiveness) {
    case "excellent":
      return "#3B7C0F";
    case "fair":
      return "#B54708";
    case "poor":
      return "#D92D20";
    case "good":
    default:
      return "#3459D4";
  }
}

function DecisionIcon({ decision }: { decision: CourseEvaluatorReviewDecision }) {
  if (decision === "accepted" || decision === "edited") {
    return <CheckCircleIcon size={14} color={colors.success[400]} weight="fill" />;
  }
  if (decision === "rejected") {
    return <XCircleIcon size={14} color={colors.error[500]} weight="fill" />;
  }
  if (decision === "synced") {
    return <CheckCircleIcon size={14} color={colors.brand[400]} weight="fill" />;
  }
  return <View style={styles.sidebarRadio} />;
}

function getItemBorderColor(decision: CourseEvaluatorReviewDecision) {
  if (decision === "accepted" || decision === "edited") return colors.success[500];
  if (decision === "rejected") return colors.error[500];
  if (decision === "synced") return colors.brand[600];
  return "transparent";
}

function renderMeta(itemId: string, meta: CourseEvaluatorReviewSidebarMeta, index: number) {
  if (meta.kind === "tag") {
    const tag = getTagPresentation(meta.tag);

    return (
      <View
        key={`${itemId}-tag-${index}`}
        style={[
          styles.sidebarPill,
          {
            backgroundColor: tag.backgroundColor,
          },
        ]}>
        <CustomText
          text={tag.label}
          useTranslationText={false}
          size="xs"
          weight="medium"
          style={{ color: tag.textColor }}
        />
      </View>
    );
  }

  if (meta.kind === "itemType") {
    const itemType = getItemTypePresentation(meta.itemType);

    return (
      <View key={`${itemId}-item-type-${index}`} style={styles.sidebarFooterTextRow}>
        {itemType.icon === "moduleDescription" && <StackSimpleIcon size={10} color={itemType.color} weight="bold" />}
        <CustomText
          text={itemType.label}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={[styles.sidebarFooterText, { color: itemType.color }]}
        />
      </View>
    );
  }

  return (
    <View key={`${itemId}-effectiveness-${index}`} style={styles.sidebarFooterTextRow}>
      <CustomText
        text={meta.effectiveness.charAt(0).toUpperCase() + meta.effectiveness.slice(1)}
        useTranslationText={false}
        size="xs"
        weight="normal"
        style={[styles.sidebarFooterText, { color: getEffectivenessColor(meta.effectiveness) }]}
      />
    </View>
  );
}

function ReviewSidebar({
  review,
  selectedItemId,
  filterMode,
  collapsedSectionIds,
  getDecision,
  onSelectItem,
  onToggleSection,
  onFilterModeChange,
}: ReviewSidebarProps) {
  return (
    <View style={styles.sidebar}>
      <Pressable
        onPress={() => onSelectItem(null)}
        role="button"
        accessibilityRole="button"
        accessibilityLabel="Select overview"
        style={({ hovered }: PressableStateCallbackType) => [
          styles.overviewButton,
          selectedItemId === null && styles.overviewButtonActive,
          hovered && selectedItemId !== null && styles.sidebarItemHover,
        ]}>
        <CustomText text="Overview" useTranslationText={false} size="s" weight="bold" style={styles.overviewLabel} />
        <View style={styles.overviewScoreWrap}>
          <CustomText
            text={`${review.score}`}
            useTranslationText={false}
            size="xs"
            weight="bold"
            style={styles.overviewScore}
          />
          <CustomText
            text={`/${review.maxScore}`}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.overviewScoreSuffix}
          />
        </View>
      </Pressable>

      <View style={styles.sidebarHeader}>
        <CustomText text="Modules" useTranslationText={false} size="s" weight="bold" style={styles.sidebarHeaderText} />
        <View style={styles.filterPills}>
          <FilterPill label="All" isActive={filterMode === "all"} onPress={() => onFilterModeChange("all")} />
          <FilterPill
            label="Undecided"
            isActive={filterMode === "undecided"}
            onPress={() => onFilterModeChange("undecided")}
          />
        </View>
      </View>

      <ScrollView
        style={styles.sidebarScroll}
        contentContainerStyle={styles.sidebarScrollContent}
        tabIndex={0}
        showsVerticalScrollIndicator>
        {review.sidebarGroups.map((group) => {
          const isCollapsed = collapsedSectionIds.has(group.id);
          const decidedInGroup = group.decided;

          return (
            <View key={group.id} style={styles.sidebarSection}>
              <View style={styles.sidebarSectionHeader}>
                <Pressable
                  onPress={() => onToggleSection(group.id)}
                  role="button"
                  accessibilityRole="button"
                  accessibilityLabel={`${isCollapsed ? "Expand" : "Collapse"} ${group.title}`}
                  style={styles.sidebarSectionToggle}>
                  {isCollapsed ? (
                    <CaretRightIcon size={14} color={colors.grey[700]} />
                  ) : (
                    <CaretDownIcon size={14} color={colors.grey[700]} />
                  )}
                  <CustomText
                    text={group.title}
                    useTranslationText={false}
                    size="xs"
                    weight="bold"
                    style={styles.sectionTitle}
                  />
                  <CustomText
                    text={`${decidedInGroup}/${group.total}`}
                    useTranslationText={false}
                    size="xs"
                    weight="normal"
                    style={decidedInGroup === group.total ? styles.sectionCountDone : styles.sectionCount}
                  />
                </Pressable>
                <PopoverMenu
                  triggerAccessibilityLabel={`${group.title} section options`}
                  triggerStyle={styles.sectionDots}
                  menuWidth={128}
                  items={[
                    {
                      key: "accept-all",
                      label: "Accept all",
                      onPress: () => {},
                    },
                    {
                      key: "skip-all",
                      label: "Skip all",
                      danger: true,
                      onPress: () => {},
                    },
                  ]}
                  renderTrigger={() => <DotsThreeIcon size={16} color={colors.grey[500]} weight="bold" />}
                />
              </View>

              {!isCollapsed &&
                group.items.map((item) => {
                  const decision = getDecision(item.id);
                  const borderColor = getItemBorderColor(decision);

                  return (
                    <Pressable
                      key={item.id}
                      onPress={() => onSelectItem(item.id)}
                      role="button"
                      accessibilityRole="button"
                      accessibilityLabel={`Select ${item.title}`}
                      style={({ hovered }: PressableStateCallbackType) => [
                        styles.sidebarItem,
                        { borderLeftWidth: 2, borderLeftColor: borderColor },
                        selectedItemId === item.id && styles.sidebarItemActive,
                        hovered && selectedItemId !== item.id && styles.sidebarItemHover,
                      ]}>
                      <View style={styles.sidebarItemRow}>
                        <DecisionIcon decision={decision} />
                        <CustomText
                          text={item.title}
                          useTranslationText={false}
                          size="xs"
                          weight="normal"
                          style={styles.sidebarItemTitle}
                        />
                        {decision === "synced" && (
                          <View style={styles.publishedPill}>
                            <CustomText
                              text="Published"
                              useTranslationText={false}
                              size="xs"
                              weight="bold"
                              style={styles.publishedPillText}
                            />
                          </View>
                        )}
                      </View>
                      {item.meta?.length ? (
                        <View style={styles.sidebarItemMeta}>
                          {item.meta.map((meta, index) => renderMeta(item.id, meta, index))}
                        </View>
                      ) : null}
                    </Pressable>
                  );
                })}
            </View>
          );
        })}
      </ScrollView>

      <View style={styles.sidebarFooter}>
        <CustomText
          text={`${review.queuedCount} queued`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.queuedText}
        />
        <CustomText
          text={`${review.skippedCount} skipped`}
          useTranslationText={false}
          size="xs"
          weight="normal"
          style={styles.skippedText}
        />
        {review.publishedCount > 0 && (
          <CustomText
            text={`${review.publishedCount} published`}
            useTranslationText={false}
            size="xs"
            weight="normal"
            style={styles.publishedText}
          />
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  sidebar: {
    width: 350,
    backgroundColor: "#FFFFFF",
    borderRightWidth: 1,
    borderRightColor: colors.grey[200],
    flexDirection: "column",
    minHeight: 0,
  },
  overviewButton: {
    minHeight: 44,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  overviewButtonActive: {
    backgroundColor: "#F5F0FE",
  },
  overviewLabel: {
    color: colors.grey[900],
  },
  overviewScoreWrap: {
    flexDirection: "row",
    alignItems: "baseline",
  },
  overviewScore: {
    color: colors.grey[900],
  },
  overviewScoreSuffix: {
    color: colors.grey[500],
  },
  sidebarHeader: {
    minHeight: 38,
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarHeaderText: {
    color: colors.grey[700],
  },
  filterPills: {
    flexDirection: "row",
    gap: 4,
  },
  sidebarScroll: {
    flex: 1,
    minHeight: 0,
    overflow: "scroll",
  },
  sidebarScrollContent: {
    paddingBottom: 16,
    flexGrow: 1,
  },
  sidebarSection: {
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[100],
  },
  sidebarSectionHeader: {
    minHeight: 39,
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  sidebarSectionToggle: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  sectionTitle: {
    color: colors.grey[700],
  },
  sectionCount: {
    color: colors.grey[500],
  },
  sectionCountDone: {
    color: colors.success[600],
  },
  sectionDots: {
    marginLeft: 12,
    padding: 4,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  sidebarItem: {
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginHorizontal: 8,
    borderRadius: 8,
  },
  sidebarItemActive: {
    backgroundColor: "#F5F0FE",
  },
  sidebarItemHover: {
    backgroundColor: colors.grey[50],
  },
  sidebarItemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  sidebarRadio: {
    width: 14,
    height: 14,
    borderRadius: 999,
    borderWidth: 1.5,
    borderColor: colors.grey[300],
    backgroundColor: "#FFFFFF",
  },
  sidebarItemTitle: {
    color: colors.grey[700],
    flex: 1,
    lineHeight: 18,
  },
  publishedPill: {
    paddingHorizontal: 10,
    height: 21,
    justifyContent: "center",
    borderRadius: 9999,
    backgroundColor: "#CCD7FF",
  },
  publishedPillText: {
    color: "#000B33",
    fontSize: 10,
  },
  sidebarItemMeta: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginLeft: 22,
    marginTop: 2,
    flexWrap: "wrap",
  },
  sidebarPill: {
    borderRadius: 8,
    paddingHorizontal: 8,
    justifyContent: "center",
    height: 24,
  },
  sidebarFooterTextRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 3,
  },
  sidebarFooterText: {
    fontSize: 11,
    lineHeight: 16,
  },
  sidebarFooter: {
    minHeight: 34,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    paddingHorizontal: 16,
    paddingVertical: 8,
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  queuedText: {
    color: "#3F7F18",
    fontSize: 11,
    lineHeight: 16,
  },
  skippedText: {
    color: "#D92D20",
    fontSize: 11,
    lineHeight: 16,
  },
  publishedText: {
    color: colors.brand[600],
    fontSize: 11,
    lineHeight: 16,
  },
});

export default ReviewSidebar;
